import Head from 'next/head';
import { NextRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import {
  onLCP,
  onINP,
  onCLS,
  onFCP,
  onTTFB,
  LCPMetricWithAttribution,
  INPMetricWithAttribution,
  CLSMetricWithAttribution,
  FCPMetricWithAttribution,
  TTFBMetricWithAttribution,
} from 'web-vitals/attribution';
import BookingCompletionScript from '../document/EngageBot/BookingCompletionScript';
import BookingStartScript from '../document/EngageBot/BookingStartScript';
import ConversionTagScript from '../document/EngageBot/ConversionTagScript';
import TrackingTagScript from '../document/EngageBot/TrackingTagScript';
import GoogleTagManagerNoScript from '../document/GoogleTagManager/GoogleTagManagerNoScript';
import GoogleTagManagerScript from '../document/GoogleTagManager/GoogleTagManagerScript';
import analytics from '../../core/analytics/analytics';

type Props = {
  router: NextRouter;
};

const Analytics = ({ router }: Props) => {
  useEffect(() => {
    if (router.pathname === '/b/[hotel]/confirmation') {
      document.getElementById('conversion-tag')?.remove();
    } else if (router.pathname === '/b/[hotel]/payment-selection') {
      document.getElementById('booking-start')?.remove();
      document.getElementById('booking-start-script')?.remove();
    } else {
      document.getElementById('booking-completion')?.remove();
      document.getElementById('booking-completion-script')?.remove();
      document.getElementById('tracking-tag')?.remove();
    }
  }, [router.pathname]);

  const onReportWebVitalMetric = useCallback(
    ({
      name,
      value,
      id,
      attribution,
    }:
      | LCPMetricWithAttribution
      | INPMetricWithAttribution
      | CLSMetricWithAttribution
      | FCPMetricWithAttribution
      | TTFBMetricWithAttribution) => {
      const parameters = {
        metric_id: id,
        event_label: name,
        metric_value: value,
        value,
      } as Record<string, unknown>;
      switch (name) {
        case 'CLS':
          parameters.debug_target = attribution?.largestShiftTarget as string;
          break;
        case 'LCP':
          parameters.debug_target = attribution?.element as string;
          break;
        case 'INP':
          parameters.debug_target = attribution?.interactionTarget as string;
          break;
        default:
          parameters.debug_target = undefined;
      }

      analytics.google.performance.webVital.track(name, parameters);
    },
    []
  );

  // Send Web Vital metric to GA4
  useEffect(() => {
    onCLS(onReportWebVitalMetric);
    onLCP(onReportWebVitalMetric);
    onINP(onReportWebVitalMetric);
    onFCP(onReportWebVitalMetric);
    onTTFB(onReportWebVitalMetric);
  }, [onReportWebVitalMetric]);

  return (
    <>
      <Head>
        {/* Engage Bot domain */}
        <meta httpEquiv="Access-Control-Allow-Origin" content="https://ac.eb.react.tokyo" />
      </Head>
      {router.pathname !== '/b/[hotel]/confirmation' && <ConversionTagScript />}
      {router.pathname === '/b/[hotel]/customer-detail' && <BookingStartScript />}
      {router.pathname === '/b/[hotel]/confirmation' && (
        <>
          <BookingCompletionScript />
          <TrackingTagScript />
        </>
      )}
      {process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID && (
        <GoogleTagManagerScript gtmId={process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID} />
      )}
      {process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID && (
        <GoogleTagManagerNoScript gtmId={process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID} />
      )}
    </>
  );
};

export default Analytics;
